import { useCallback, useMemo } from 'react';

import { getSourceEventParams, useAmplitude } from '@crehana/analytics';
import Lock from '@crehana/compass.icons.outline/Lock';
import { useOrganizationAccessLevel } from '@crehana/compass.knowledgehub/hooks';
import { useFeatureFlag } from '@crehana/feature-flags/react';
import { useTranslation } from '@crehana/i18n';
import { CapitalizeFirstLetter } from '@crehana/utils';
import {
  type ExternalRouteType as ExternalRoute,
  flattenRoutes,
  type RouteType as Route,
  type SingleRouteType as SingleRoute,
} from '@crehana/web';
import { useRouter } from 'next/router';

import { FEATURE_FLAGS_V5 } from '@/shared/components/AdmindashboardView/featureFlags';
import type { BaseRouteType } from '@/shared/components/DashboardTemplate/types';
import useConfiguration from '@/views/AdminDashboard/hooks/useConfiguration';
import useHigherEducation from '@/views/AdminDashboard/hooks/useHigherEducation';
import useManageRoutes from '@/views/AdminDashboard/hooks/useManageRoutes';
import { useGetFlagOrganization } from '@/views/AdminDashboard/pages/Hana/hooks';
import { ROUTE_KEY } from '@/views/AdminDashboard/routes';
import { ROUTE_KEY as ROUTE_KEY_TALENT } from '@/views/AdminDashboard/routesTalent';

import useFeedbackRoute from '../useFeedbackRoute';
import { AMPLITUDE_DASHBOARDMENU } from './amplitude';
import { AMPLITUDE_DASHBOARDMENU_ENUM } from './types';

const useNewRoutes = (
  routes: BaseRouteType[],
  secondaryRoutes: BaseRouteType[],
  baseUpperRoutes: BaseRouteType[],
) => {
  const { t } = useTranslation();
  const { logEvent } = useAmplitude();
  const { pathname, push, query } = useRouter();
  const { companyId, ssoToken, redirect } = useFeedbackRoute();
  const { hasAutomationRules, hasCustomFields, isTeamsActive } =
    useManageRoutes();
  const { isHigherEducationActive } = useHigherEducation();

  const { flagValue: isHanaAIEnabled } = useFeatureFlag(
    FEATURE_FLAGS_V5.MODULE_HANA_AI,
  );
  const { isFullSuite } = useGetFlagOrganization();

  const { flagValue: isHanaAIExperimentEnabled } = useFeatureFlag(
    FEATURE_FLAGS_V5.ACTIVE_HANA_AI,
  );
  const { flagValue: isHanaAIAssistantEnabled } = useFeatureFlag(
    FEATURE_FLAGS_V5.ENABLE_HANA_END_USER,
  );

  const { slug, teamId } = query as { slug: string; teamId: string };

  const {
    canUseElevate,
    casUseGamification,
    canUseLiveSessions,
    originalId: organizationId,
  } = useConfiguration();

  const { isPureLMSOrganization } = useOrganizationAccessLevel({
    orgId: organizationId,
  });

  const getPath = (path: string, key: string) => {
    if (key === ROUTE_KEY.FEEDBACK) {
      return path
        .replace('[companyId]', companyId)
        .replace('[ssoToken]', ssoToken)
        .replace('[redirect]', redirect);
    }
    return path;
  };

  const mapRoute = (routes: BaseRouteType[]) => {
    return routes
      .filter(route => {
        if (!isHigherEducationActive) {
          return route.key !== ROUTE_KEY.MARKET;
        }
        return route;
      })
      .filter(route => {
        if (!isHanaAIExperimentEnabled && !isFullSuite) {
          return route.key !== ROUTE_KEY.HANA_AI;
        }
        return route;
      })
      .filter(route => {
        if (!isHanaAIAssistantEnabled) {
          return route.key !== ROUTE_KEY.HANA_AI_ASSISTANT;
        }
        return route;
      })
      .map(route => {
        const baseRoute = {
          key: route.key,
          tourSelector: route.tourSelector,
          icon: route.icon,
          path: getPath(route.path, route.key),
          exact: true,
          startExpanded: pathname.includes(route.path),
          label: t(route.label),
          activeIcon: route.icon,
          closedOnCollapse: true,
          openInNewTab: route.openInNewTab,
          rightIcon: route.rightIcon,
          decorations: {
            subTitle: route.decorations?.subTitle
              ? t(route.decorations?.subTitle)
              : '',
            lineTop: route.decorations?.lineTop,
          },
        };
        if (!casUseGamification && route.key === ROUTE_KEY.GAMIFICATION) {
          const baseRoutewithoutGamification = {
            ...baseRoute,
            rightIcon: Lock,
            hideNested: true,
          };
          return baseRoutewithoutGamification;
        }

        if (
          !isHanaAIEnabled &&
          route.key === ROUTE_KEY_TALENT.HANA_AI &&
          !isFullSuite
        ) {
          const baseRoutewithoutHanaAI = {
            ...baseRoute,
            rightIcon: Lock,
          };
          return baseRoutewithoutHanaAI;
        }

        // biome-ignore lint/style/useBlockStatements: single-line early return
        if (route.hideNested) return baseRoute;

        if (route.nested && route.nested.length > 0) {
          return {
            ...baseRoute,
            nested: route.nested
              .filter(filterRoute => {
                // biome-ignore lint/style/useBlockStatements: single-line early return
                if (!isTeamsActive)
                  return filterRoute.key !== ROUTE_KEY.MANAGE_TEAMS;
                // biome-ignore lint/style/useBlockStatements: single-line early return
                else return filterRoute;
              })
              .filter(filterRoute => {
                // biome-ignore lint/style/useBlockStatements: single-line early return
                if (!hasAutomationRules)
                  return filterRoute.key !== ROUTE_KEY.MANAGE_AUTOMATION;
                // biome-ignore lint/style/useBlockStatements: single-line early return
                else return filterRoute;
              })
              .filter(filterRoute => {
                if (!isHigherEducationActive) {
                  return filterRoute.key !== ROUTE_KEY.PATHS_ECOMMERCE;
                  // biome-ignore lint/style/useBlockStatements: single-line early return
                } else return filterRoute;
              })
              .filter(filterRoute => {
                if (isPureLMSOrganization) {
                  return filterRoute.key !== ROUTE_KEY.KNOWLEDGE_HUB_CATALOG;
                  // biome-ignore lint/style/useBlockStatements: single-line early return
                } else return filterRoute;
              })
              .map(nestedRoute => {
                // Show the Elevate Courses nested route with the Lock Icon if the user doesn't have the permission
                if (ROUTE_KEY.ELEVATE_COURSES === nestedRoute.key) {
                  return {
                    key: nestedRoute.key,
                    tourSelector: nestedRoute.tourSelector,
                    label: t(nestedRoute.label),
                    icon: route.icon,
                    rightIcon: canUseElevate
                      ? undefined
                      : nestedRoute.rightIcon,
                    activeIcon: route.icon,
                    path: nestedRoute.path,
                    exact: true,
                  };
                }

                // Show the Live Sessions nested route with the Lock Icon if the user doesn't have the permission
                if (ROUTE_KEY.SESSIONS_LIVES === nestedRoute.key) {
                  return {
                    key: nestedRoute.key,
                    tourSelector: nestedRoute.tourSelector,
                    label: t(nestedRoute.label),
                    icon: route.icon,
                    rightIcon: canUseLiveSessions
                      ? undefined
                      : nestedRoute.rightIcon,
                    activeIcon: route.icon,
                    path: nestedRoute.path,
                    exact: true,
                  };
                }

                // Show the Knowledge Hub Documents nested route with the Lock Icon if the user doesn't have the permission
                if (ROUTE_KEY.KNOWLEDGE_HUB_DOCUMENTS === nestedRoute.key) {
                  return {
                    key: nestedRoute.key,
                    tourSelector: nestedRoute.tourSelector,
                    label: t(nestedRoute.label),
                    icon: route.icon,
                    rightIcon: canUseElevate ? undefined : Lock,
                    activeIcon: route.icon,
                    path: nestedRoute.path,
                    exact: true,
                  };
                }

                // Show the Gallery Certificates item nested route with the Lock Icon if the user doesn't have the permission
                if (
                  ROUTE_KEY.CONTENT_CERTIFICATES_GALLERY === nestedRoute.key
                ) {
                  return {
                    key: nestedRoute.key,
                    tourSelector: nestedRoute.tourSelector,
                    label: t(nestedRoute.label),
                    icon: route.icon,
                    rightIcon: canUseElevate
                      ? undefined
                      : nestedRoute.rightIcon,
                    activeIcon: route.icon,
                    path: nestedRoute.path,
                    exact: true,
                  };
                }

                return {
                  key: nestedRoute.key,
                  tourSelector: nestedRoute.tourSelector,
                  label: t(nestedRoute.label),
                  icon: route.icon,
                  activeIcon: route.icon,
                  path: nestedRoute.path,
                  exact: true,
                };
              })
              .filter(filterRoute => {
                // biome-ignore lint/style/useBlockStatements: single-line early return
                if (!hasCustomFields)
                  return filterRoute.key !== ROUTE_KEY.MANAGE_CUSTOM_FIELDS;
                // biome-ignore lint/style/useBlockStatements: single-line early return
                else return filterRoute.key !== ROUTE_KEY.MANAGE_ATTRIBUTES;
              })
              .map(nestedRoute => ({
                key: nestedRoute.key,
                tourSelector: nestedRoute.tourSelector,
                label: t(nestedRoute.label),
                icon: route.icon,
                activeIcon: route.icon,
                rightIcon: nestedRoute.rightIcon,
                path: nestedRoute.path,
                exact: true,
              })),
          };
          // biome-ignore lint/style/useBlockStatements: single-line early return
        } else return baseRoute;
      });
  };

  const mappedMainRoutes = mapRoute(routes);
  const mappedSecondaryRoutes = mapRoute(secondaryRoutes);
  const mappedUpperRoutes = mapRoute(baseUpperRoutes);

  const mappedRoutes = useMemo<Route[]>(
    () => mappedMainRoutes as Route[],
    [mappedMainRoutes],
  );

  const upperRoutes = useMemo<Route[]>(
    () => mappedUpperRoutes as Route[],
    [mappedUpperRoutes],
  );

  const mappedBottomRoutes = useMemo<Route[]>(
    () => mappedSecondaryRoutes as Route[],
    [mappedSecondaryRoutes],
  );

  const flatRoutes = useMemo<SingleRoute[]>(
    () =>
      flattenRoutes([...mappedRoutes, ...mappedBottomRoutes, ...upperRoutes]),
    [mappedRoutes, mappedBottomRoutes, upperRoutes],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  const currentSingleRoute = useMemo<SingleRoute>(() => {
    const firstRoute = mappedRoutes[0] as SingleRoute;

    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (pathname === '/') return firstRoute;

    const currentRoute = flatRoutes.find(
      r => r.path === pathname || `${pathname}/`.endsWith(r.path),
    );

    return currentRoute || firstRoute;
  }, [flatRoutes, pathname]);

  const logNavigation = (route: SingleRoute | ExternalRoute) => {
    const sections = pathname.replace('/org/[slug]/panel/', '').split('/');
    const url = pathname.replace('[slug]', slug);
    const event = AMPLITUDE_DASHBOARDMENU[parseURL(route)];

    if (event) {
      logEvent({
        event,
        data: {
          ...getSourceEventParams(),
          Organization: slug,
          'Section Menu': CapitalizeFirstLetter(sections[0]) || '',
          Tab: sections[1] && CapitalizeFirstLetter(sections[1]),
          Url: url,
        },
      });
    }
  };

  const parseURL = (route: SingleRoute | ExternalRoute) => {
    const amplitudeKey = route.path
      .toUpperCase()
      // TODO: talk with the code owner to fix this code so we can enable this rule again
      // eslint-disable-next-line no-useless-escape
      .replace(/\-/g, '_')
      .split('/')[4] as AMPLITUDE_DASHBOARDMENU_ENUM;

    try {
      const { hostname } = new URL(route.path);
      if (hostname === 'canny.io') {
        return AMPLITUDE_DASHBOARDMENU_ENUM.FEEDBACK;
      } else if (hostname === 'ayuda.crehana.com') {
        return AMPLITUDE_DASHBOARDMENU_ENUM.AYUDA;
      } else {
        return amplitudeKey;
      }
    } catch (e) {
      return amplitudeKey;
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  const selectPageOrOpenLink = useCallback(
    (route: SingleRoute | ExternalRoute) => {
      logNavigation(route);

      // biome-ignore lint/style/useBlockStatements: single-line early return
      if (route?.openInNewTab) window.open(route.path, '_blank');
      else if (route.key === ROUTE_KEY.HANA_AI_ASSISTANT) {
        window.location.href = route.path.replace('[slug]', slug);
      } else if (route.key === ROUTE_KEY.HANA_AI) {
        const routeSlug = route.path.replace('[slug]', slug);
        push({
          pathname: routeSlug,
          query: { 'crehana-ai': 'true' },
        });
      } else {
        const routeSlug = route.path.replace('[slug]', slug);
        const isLeadershipPanel = route.path.includes('[teamId]');

        if (isLeadershipPanel) {
          // biome-ignore lint/style/useBlockStatements: single-line early return
          if (teamId) push(routeSlug.replace('[teamId]', teamId));
          // biome-ignore lint/style/useBlockStatements: single-line early return
          else push(`/org/${slug}/panel/lider/`);
          // biome-ignore lint/style/useBlockStatements: single-line early return
        } else push(routeSlug);
      }
    },
    [slug, teamId, push],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  const onClickItem = useCallback(
    (route: string) => {
      /**
       * Modify the utils getMenuLinks from crehana/web
       * and create a interface similar to Routes.
       */
      const routeSlug = route.replace('[slug]', slug);
      push(routeSlug);

      // if (route?.openInNewTab) window.open(route.path, '_blank');
      // else {
      //   const isLeadershipPanel = route.path.includes('[teamId]');

      //   if (isLeadershipPanel) {
      //     if (teamId) push(routeSlug.replace('[teamId]', teamId));
      //     else push(`/org/${slug}/panel/lider/`);
      //   } else push(routeSlug);
      // }
    },
    [slug, teamId],
  );

  return {
    mappedRoutes,
    flatRoutes,
    currentSingleRoute,
    mappedBottomRoutes,
    selectPageOrOpenLink,
    onClickItem,
    upperRoutes,
  };
};

export default useNewRoutes;
